import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Spin, Table, Tag, message } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { adminRestaurantReservationService } from '../../services/adminService';
import { socketService } from '../../services/socketService';
import './ServiceReservations.css';

interface RestaurantReservation {
  id: string;
  full_name: string;
  email: string;
  reservation_date: string;
  reservation_time: string;
  number_of_guests: number;
  special_request?: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  created_at: string;
}

const RestaurantReservations: React.FC = () => {
  const [reservations, setReservations] = useState<RestaurantReservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState('');
  const [editingReservation, setEditingReservation] = useState<RestaurantReservation | null>(null);
  const [editVisible, setEditVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminRestaurantReservationService.getAll(100, 0, statusFilter, searchText || undefined);
      const rows = Array.isArray(response.data) ? (response.data as RestaurantReservation[]) : [];
      setReservations(rows);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch restaurant reservations');
    } finally {
      setLoading(false);
    }
  }, [searchText, statusFilter]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    const socket = socketService.connect();

    const onNotificationCreated = (event: any) => {
      const eventType = event?.eventType || event?.payload?.eventType;
      if (eventType === 'new_restaurant_reservation') {
        fetchReservations();
      }
    };

    socket.on('notification_created', onNotificationCreated);

    return () => {
      socket.off('notification_created', onNotificationCreated);
    };
  }, [fetchReservations]);

  const openEdit = (reservation: RestaurantReservation) => {
    setEditingReservation(reservation);
    form.setFieldsValue({
      name: reservation.full_name,
      email: reservation.email,
      date: reservation.reservation_date,
      time: reservation.reservation_time,
      guests: reservation.number_of_guests,
      notes: reservation.special_request,
      status: reservation.status,
    });
    setEditVisible(true);
  };

  const submitEdit = async (values: any) => {
    if (!editingReservation) return;
    try {
      setLoading(true);
      await adminRestaurantReservationService.update(editingReservation.id, values);
      message.success('Reservation updated');
      setEditVisible(false);
      fetchReservations();
    } catch (error) {
      console.error(error);
      message.error('Failed to update reservation');
    } finally {
      setLoading(false);
    }
  };

  const confirmReservation = async (id: string) => {
    try {
      setLoading(true);
      await adminRestaurantReservationService.confirm(id);
      message.success('Reservation confirmed');
      fetchReservations();
    } catch (error) {
      console.error(error);
      message.error('Failed to confirm reservation');
    } finally {
      setLoading(false);
    }
  };

  const cancelReservation = async (id: string) => {
    try {
      setLoading(true);
      await adminRestaurantReservationService.cancel(id);
      message.success('Reservation cancelled');
      fetchReservations();
    } catch (error) {
      console.error(error);
      message.error('Failed to cancel reservation');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      pending: 'orange',
      confirmed: 'green',
      completed: 'blue',
      cancelled: 'red',
    };
    return map[status] || 'default';
  };

  const columns = [
    {
      title: 'Guest',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 180,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: 'Date',
      dataIndex: 'reservation_date',
      key: 'reservation_date',
      width: 120,
      render: (value: string) => value ? dayjs(value).format('MMM D, YYYY') : 'N/A',
    },
    {
      title: 'Time',
      dataIndex: 'reservation_time',
      key: 'reservation_time',
      width: 120,
    },
    {
      title: 'Guests',
      dataIndex: 'number_of_guests',
      key: 'number_of_guests',
      width: 90,
      align: 'center' as const,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (value: string) => (
        <Tag color={getStatusColor(value)} style={{ textTransform: 'capitalize' }}>
          {value}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: unknown, record: RestaurantReservation) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
          {record.status === 'pending' ? (
            <Button icon={<CheckOutlined />} type="primary" onClick={() => confirmReservation(record.id)} />
          ) : null}
          {record.status !== 'cancelled' ? (
            <Button icon={<CloseOutlined />} danger onClick={() => cancelReservation(record.id)} />
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div className="service-reservations-container">
      <div className="service-reservations-header">
        <h1>Restaurant Table Reservations</h1>
        <p>Manage reservation requests submitted from the restaurant form.</p>
      </div>

      <div className="service-reservations-filters">
        <Input
          placeholder="Search by guest or email"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          style={{ width: 320 }}
          allowClear
        />
        <Select
          placeholder="Filter by status"
          value={statusFilter}
          onChange={setStatusFilter}
          allowClear
          style={{ width: 220 }}
          options={[
            { label: 'Pending', value: 'pending' },
            { label: 'Confirmed', value: 'confirmed' },
            { label: 'Completed', value: 'completed' },
            { label: 'Cancelled', value: 'cancelled' },
          ]}
        />
        <Button onClick={fetchReservations} loading={loading}>Refresh</Button>
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={reservations}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1100 }}
        />
      </Spin>

      <Modal
        title="Edit Table Reservation"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={submitEdit}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="date" label="Reservation Date" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
          <Form.Item name="time" label="Reservation Time" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="guests" label="Number of Guests" rules={[{ required: true }]}>
            <Input type="number" min={1} />
          </Form.Item>
          <Form.Item name="notes" label="Special Request">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'Pending', value: 'pending' },
                { label: 'Confirmed', value: 'confirmed' },
                { label: 'Completed', value: 'completed' },
                { label: 'Cancelled', value: 'cancelled' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RestaurantReservations;
